import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { organizationDataAtom } from '@/store'
import { budgetStatusQueryOptions } from '@/lib/query-options'
import { Progress } from '@/components/ui/progress'
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol'
import type { PlatformAllocation } from '@/lib/types'

function getUtilizationColor(pct: number): string {
  if (pct >= 100) return 'bg-red-600'
  if (pct >= 80) return 'bg-amber-500'
  return 'bg-[#290BD9]'
}

function getUtilizationTextColor(pct: number): string {
  if (pct >= 100) return 'text-red-700'
  if (pct >= 80) return 'text-amber-700'
  return 'text-gray-700'
}

export function BudgetUtilizationBar() {
  const organizationData = useAtomValue(organizationDataAtom)
  const { symbol: _cs } = useCurrencySymbol()
  const currencySymbol = _cs ?? '€'

  const { data: budgetStatus } = useQuery({
    ...budgetStatusQueryOptions(organizationData?.id),
  })

  if (!budgetStatus) return null

  const entries = Object.entries(budgetStatus.allocated) as [string, PlatformAllocation][]
  if (entries.length === 0) return null

  return (
    <div className="space-y-2">
      {entries.map(([platform, allocation]) => {
        const pct = allocation.utilization_pct
        const barColor = getUtilizationColor(pct)
        const textColor = getUtilizationTextColor(pct)
        const label = platform.charAt(0).toUpperCase() + platform.slice(1)

        return (
          <div key={platform} className="rounded-lg border bg-white px-4 py-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-900">
                {label} Ads: {currencySymbol}{allocation.campaign_total_monthly.toLocaleString()} of {currencySymbol}{allocation.budget.toLocaleString()} allocated
              </span>
              <span className={`text-sm font-semibold ${textColor}`}>
                {pct}%
              </span>
            </div>
            <Progress
              value={Math.min(pct, 100)}
              className="h-2 bg-gray-100"
              indicatorClassName={barColor}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-gray-500">
                {allocation.campaign_count} campaign{allocation.campaign_count !== 1 ? 's' : ''} · {currencySymbol}{allocation.campaign_total_daily.toLocaleString()}/day
              </span>
              <span className="text-xs text-gray-500">
                {currencySymbol}{allocation.remaining_monthly.toLocaleString()} remaining
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
